import { useTranslation } from 'react-i18next';
import { PageContainer } from '@/components/layout/PageContainer';
import { AdminPageHeader } from '@/components/admin/shared/AdminPageHeader';
import { Icon } from '@/components/common/Icon';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { buildNameInitials } from '@/lib/nameInitials';
import { formatPhoneNumberForDisplay } from '@/utils/phoneNumbers';

export function AdminProfile() {
  const { t } = useTranslation(['admin', 'common']);
  const { currentUser, logOut } = useAuth();

  if (currentUser === null) {
    return null;
  }

  return (
    <PageContainer width="wide">
      <AdminPageHeader title={t('admin:profile.title')} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        <Card className="flex items-center gap-4">
          {currentUser.profilePictureUrl === null ? (
            <div className="w-16 h-16 rounded-full bg-zooviolet text-zoowhite flex items-center justify-center font-bold text-xl">
              {buildNameInitials(currentUser.fullName)}
            </div>
          ) : (
            <img
              src={currentUser.profilePictureUrl}
              alt={currentUser.fullName}
              className="w-16 h-16 rounded-full object-cover border-2 border-zoowhite shadow-sm"
            />
          )}
          <div>
            <div className="text-lg font-bold text-zooindigo">{currentUser.fullName}</div>
            <div className="mt-1">
              <Badge tone="accent" size="sm">
                {t('admin:role.platformAdmin')}
              </Badge>
            </div>
          </div>
        </Card>

        <Card className="space-y-4">
          <div className="flex items-center gap-2 text-zooindigo font-semibold">
            <Icon name="User" className="text-zooviolet" />
            {t('admin:profile.accountDetails')}
          </div>
          <ProfileField label={t('admin:profile.emailLabel')} value={currentUser.email} />
          <ProfileField
            label={t('admin:profile.phoneLabel')}
            value={
              currentUser.phone === null
                ? t('admin:profile.notProvided')
                : formatPhoneNumberForDisplay(currentUser.phone)
            }
          />
          <ProfileField
            label={t('admin:profile.languageLabel')}
            value={currentUser.preferredLanguage.toUpperCase()}
          />
        </Card>
      </div>

      <Button
        variant="linkDanger"
        onClick={function handleLogout() {
          logOut();
        }}
        className="mt-5 flex items-center gap-2 text-sm"
      >
        <Icon name="LogOut" />
        {t('common:actions.logout')}
      </Button>
    </PageContainer>
  );
}

interface ProfileFieldProps {
  label: string;
  value: string;
}

function ProfileField({ label, value }: ProfileFieldProps) {
  return (
    <div className="border-t border-zooborder pt-3 first:border-t-0 first:pt-0">
      <div className="text-[10px] font-bold text-zoomuted uppercase tracking-wider mb-0.5">
        {label}
      </div>
      <div className="text-sm text-zooindigo">{value}</div>
    </div>
  );
}
